import { findRoleByLabel, getSignerRoleLabel, signingOrderFromRoles } from 'libs.consent.common';
/**
 * Editor stores geometry as fractions of the page (0–1). Template `form.json` uses CSS percentages (0–100).
 */
function fractionToCssPercent(fraction) {
    return Math.round(fraction * 10000) / 100;
}
function signatureFieldOrder(signerRole, roles, leftover) {
    return signingOrderFromRoles(signerRole, roles) ?? leftover ?? '0';
}
/**
 * Maps placed elements to `signatureFields` expected by `templates.consent.framework.app~1_0` for PDF mode.
 * Only {@link ConsentElement} types that participate in signing are included (signature-like fields).
 *
 * `ec__key` is included so the host (the client app embedding the consent
 * bundle) can correlate a signature submission back to the corresponding
 * `ec__requested_signatures[].ec__key` on the document template, which is
 * what Cortex's signing endpoints (e.g. `ec__signed_documents.sign`) expect
 * as `ec__signature_identifier`. Without it the host has no stable mapping.
 */
export function consentElementsToSignatureFields(elements, roles = []) {
    const SIGNATURE_LIKE = ['SIGNATURE', 'INITIALS'];
    return elements
        .filter((el) => SIGNATURE_LIKE.includes(el.type))
        .map((el) => {
        // Cortex's signing endpoints (`ec__signed_documents.sign`) reject
        // an empty `ec__signature_identifier`, which is sourced from this
        // value at the host. Fail loudly so a missing key surfaces in the
        // studio author flow rather than as an opaque server-side rejection
        // during participant signing.
        if (typeof el.config.ec__key !== 'string' || el.config.ec__key.length === 0) {
            throw new Error(`Consent bundler: signature element is missing ec__key (type=${el.type}, role=${el.config.ec__signer_role ?? ''}). Every signature must have a stable ec__key — Cortex rejects empty ec__signature_identifier.`);
        }
        return {
            ec__key: el.config.ec__key,
            page: el.page,
            xPct: fractionToCssPercent(el.position.xPct),
            yPct: fractionToCssPercent(el.position.yPct),
            widthPct: fractionToCssPercent(el.position.widthPct),
            heightPct: fractionToCssPercent(el.position.heightPct),
            type: el.type,
            signerType: findRoleByLabel(el.config.ec__signer_role, roles)?.signerTypeId ?? 'participant',
            role: getSignerRoleLabel(el.config.ec__signer_role),
            optional: el.config.ec__optional ?? false,
            placeholder: el.config.ec__placeholder ?? '',
            order: signatureFieldOrder(el.config.ec__signer_role, roles, el.config.ec__order),
            label: el.config.ec__label ?? '',
            title: el.config.ec__title ?? '',
            description: el.config.ec__description ?? '',
            customData: el.config.ec__custom_data ?? [],
        };
    });
}
